/**
 * Main application layout that provides the core UI structure and navigation framework.
 *
 * Key features:
 * - Responsive navigation with collapsible sidebar for mobile devices
 * - Dynamic document title management based on current route context
 * - Contextual search integration with optional display control
 * - Scenario-aware routing with workspace and origin context preservation
 * - Scroll locking mechanism for improved mobile UX when sidebar is open
 *
 * @param {boolean} contextSearch - Controls the visibility of the search autocomplete component
 * @param {React.ReactNode} children - Content to be rendered within the layout structure
 */

import React, {
  FC,
  ClassAttributes,
  useState,
  useMemo,
  useRef,
  useEffect,
  useContext,
} from "react";
import cx from "classnames";
import { debounce } from "lodash";
import { Step } from "react-joyride";
import { useTranslation } from "react-i18next";
import { Icon } from "@foris/avocado-icons";
import { Drawer, ThemeStore } from "@foris/avocado-suite";
import { useHistory, useLocation } from "react-router-dom";
import { useScrollLock } from "@common/hooks/useScrollLock";
import useNavigationUrl from "@common/hooks/useNavigationUrl";
import useContextUrl from "@common/hooks/useContextUrl";
import SidebarNavigation from "./components/sidebar-navigation";
import UserMenu from "./components/user-menu";
import ContextScenario from "./components/context-scenario";
import ContextSearch from "./components/context-search";
import useClickOutside from "@common/hooks/useClickOutside";
import TourTooltip from "@common/components/tour-tooltip";
import OnboardingVideoModal from "@common/components/onboarding-video-modal";
import TourTooltipBeacon from "@common/components/tour-tooltip-beacon";
import {
  BURGER_MENU_ONBOARDING,
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
  USER_MENU_ONBOARDING,
} from "@context/useOnboardingStore";
import useGetModulesUrls from "@common/hooks/useGetModulesUrls";
import { setVisibleHeaderPixels } from "@context/useLayoutStore";
import { useChatAgentPanelStore, setShowPanel } from "@context/useChatAgentPanel";
import HeaderLogo from "./components/header-logo";
import { ChatPanelMenu, ChatPanel, ChatSessionProvider } from "@foris/ai-agent";
import { renderNotFront } from "@foris/avocado-not-front";
import { useLayoutStore } from "@context/useLayoutStore";
import { ContextEDH } from "@context/ContextEDH";
import { useIdentityTokenStore } from "@context/useIdentityTokenStore";
import { useSettingsStore } from "@context/useSettingsStore";
import * as keycloakServices from "@services/collections/identity/keycloakServices";
import "@assets/scss/app.scss";
import "@assets/css/vendor.scss";
import "@assets/scss/customOverrides.scss";
import css from "./layout.module.scss";

interface LayoutProps extends ClassAttributes<any> {
  contextSearch: boolean;
  children?: React.ReactNode | undefined;
}

const Layout: FC<LayoutProps> = ({ contextSearch, children }) => {
  const menuRef = useRef(null);
  const location = useLocation();
  const history = useHistory();
  const [isContextModalOpen, setIsContextModalOpen] = useState(false);
  const { workspace, scenario, origin } = useContextUrl();
  const { homeUrl } = useNavigationUrl();
  const { canAccessEDH, isBaseDataLoaded, hasSomeModulesAccesibles, isBookingAccesible } =
    useGetModulesUrls();
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isShowUserMenu, setIsShowUserMenu] = useState(false);
  const [showBurgerMenuTour, setShowBurgerMenuTour] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const onboardinsSeen = useOnboardingStore(state => state.onboardingsSeen);
  const showPanel = useChatAgentPanelStore(state => state.showPanel);
  const visibleHeaderPixels = useLayoutStore(state => state.visibleHeaderPixels);
  const { state } = useContext(ContextEDH);
  const locale = state.base.locale;
  const themeMode = ThemeStore.useThemeStore(state => state.theme.mode);
  const hasMultitenant =
    (import.meta.env.VITE_HAS_MULTITENANT ?? import.meta.env.REACT_APP_HAS_MULTITENANT) === "true";
  const identityToken = useIdentityTokenStore(state => state.identityToken);
  const [originalTenantCode] = window?.location?.hostname?.split(".") ?? [];
  const isAgentsChatEnabled = useSettingsStore(state => state.isAgentsChatEnabled);
  const isChatAgentVisible = hasMultitenant && Boolean(identityToken) && isAgentsChatEnabled;
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateVisiblePixels = () => {
      if (!headerRef.current) return;
      const rect = headerRef.current.getBoundingClientRect();
      const visible = Math.max(
        0,
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
      );
      setVisibleHeaderPixels(visible);
    };

    const handleDebounceScroll = debounce(updateVisiblePixels, 200);

    window.addEventListener("scroll", handleDebounceScroll, { passive: true });
    updateVisiblePixels(); // initial

    return () => {
      window.removeEventListener("scroll", handleDebounceScroll);
    };
  }, []);

  useEffect(() => {
    keycloakServices.startAutoRefresh();
    return () => keycloakServices.dispose();
  }, [isAgentsChatEnabled]);

  const showHotspot = useMemo(() => {
    return shouldShowHotspot(BURGER_MENU_ONBOARDING) && !isContextModalOpen;
  }, [onboardinsSeen, isContextModalOpen]);

  const showUserMenuHotspot = useMemo(() => {
    return shouldShowHotspot(USER_MENU_ONBOARDING) && !isContextModalOpen;
  }, [onboardinsSeen, isContextModalOpen]);

  // Determines current module context for title and UI customization
  const isInBooking = location?.pathname.includes("booking");

  // Constructs context-aware home URL preserving workspace, scenario, and origin parameters
  const urlHome = workspace && scenario && origin ? homeUrl(workspace, scenario, origin) : "/home";

  // Prevents main content scrolling when mobile navigation is active
  useScrollLock(isSidebarOpen);

  const isEdhPath = useMemo(() => {
    const regex = /\/(home|auth|catalogs|reports)/;
    return !regex.test(location?.pathname) && location?.pathname !== "/" && !isInBooking;
  }, [location?.pathname]);

  if (!canAccessEDH && isEdhPath) {
    history.replace(urlHome);
  }

  useClickOutside({
    ref: menuRef,
    onClickOutside: () => setIsShowUserMenu(false),
  });

  const steps: Step[] = [
    {
      content: t("onboarding.user-menu.description"),
      target: ".tourUserMenu",
      locale: {
        last: t("onboarding.actions.done"),
      },
      title: t("onboarding.user-menu.title"),
      disableBeacon: false,
      placement: "bottom-start",
      placementBeacon: "bottom-start",
    },
  ];

  return (
    <>
      {showUserMenuHotspot && (
        <TourTooltip
          onClose={() => {
            setIsOnboardingOpen(false, USER_MENU_ONBOARDING);
          }}
          onToolipOpen={() => {
            setIsOnboardingOpen(true, USER_MENU_ONBOARDING);
          }}
          startTour={showUserMenuHotspot}
          steps={steps}
          spotlightPadding={-2}
        />
      )}

      {showHotspot && (
        <OnboardingVideoModal
          isOpen={showBurgerMenuTour}
          title={t("onboarding.burger-menu.title")}
          description={t("onboarding.burger-menu.description")}
          videoUrl="https://files.foris.ai/darwin/onboarding/Menu+Lateral+Contextualizado+-+PROD.mp4"
          onClose={() => {
            setShowBurgerMenuTour(false);
            setIsOnboardingOpen(false, BURGER_MENU_ONBOARDING);
          }}
        />
      )}

      <section className={css.layout}>
        {/* Top fixed menu (mobile/sidebar behavior) */}
        <SidebarNavigation isVisible={isSidebarOpen} onSidebarToggle={setIsSidebarOpen} />

        <header className={cx(css.header, "container-row", "row--noWrap")} ref={headerRef}>
          {/* Burger menu toggle */}
          <div className={css.btnMenuContainer}>
            {isBaseDataLoaded && !hasSomeModulesAccesibles && isBookingAccesible ? null : (
              <button className={css.btnMenu} onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                <Icon name="menu" size="lg" color="neutral-50" />
              </button>
            )}

            {showHotspot && (
              <button
                className={css.btnBurgerTour}
                onClick={() => {
                  setShowBurgerMenuTour(true);
                  setIsOnboardingOpen(true, BURGER_MENU_ONBOARDING);
                }}
              >
                <TourTooltipBeacon />
              </button>
            )}
          </div>

          {/* Logo with link to contextual home */}
          <HeaderLogo urlHome={urlHome} />

          {/* Optional contextual search bar */}
          <div className={css.cntInfo}>
            {!!contextSearch && <ContextSearch className={css.cntInfo_autocomplete} />}
          </div>

          {/* Context scenario selector */}
          <ContextScenario onContextModalOpen={setIsContextModalOpen} />

          {/* Activate chat panel menu */}
          {isChatAgentVisible && (
            <div className={css.containerChatPanelMenu}>
              <ChatPanelMenu
                handleClick={() => {
                  setShowPanel(!showPanel);
                }}
              />
            </div>
          )}
          {/* Top-level main menu */}
          <div ref={menuRef} className="tourUserMenu">
            <button
              onClick={() => setIsShowUserMenu(!isShowUserMenu)}
              className={cx(css.containerUser, isShowUserMenu && css.containerUser__active)}
            >
              <div className={css.containerUser_btn}>
                <Icon name="user" filled color="neutral-00" />
              </div>
            </button>
            {isShowUserMenu && <UserMenu />}
          </div>
        </header>
        {isChatAgentVisible && (
          <ChatSessionProvider>
            <Drawer
              bodyPadding={false}
              closeOnOverlayClick
              contentWidth="default"
              className={css.chatPanelContent}
              contentClassName={css.chatPanelContent}
              heightGap={visibleHeaderPixels}
              isOpen={showPanel}
              isExpanded={expanded}
              onClose={() => setShowPanel(false)}
            >
              <ChatPanel
                style={{ maxHeight: "100%" }}
                dataSource={{
                  url: `${import.meta.env.VITE_DARWIN_AGENTS_URL ?? import.meta.env.REACT_APP_DARWIN_AGENTS_URL}/api/v1/chat-streaming/`,
                  resumeUrl: `${import.meta.env.VITE_DARWIN_AGENTS_URL ?? import.meta.env.REACT_APP_DARWIN_AGENTS_URL}/api/v1/chat/resume/`,
                  method: "post",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${identityToken}`,
                    tenant: originalTenantCode,
                  },
                }}
                locale={locale}
                theme={themeMode}
                renderNotFront={renderNotFront}
                initialSuggestions={[
                  {
                    key: "suggestion-1",
                    title: t("agent-chat.suggestions.suggestion-1"),
                  },
                  {
                    key: "suggestion-2",
                    title: t("agent-chat.suggestions.suggestion-2"),
                  },
                  {
                    key: "suggestion-3",
                    title: t("agent-chat.suggestions.suggestion-3"),
                  },
                  {
                    key: "suggestion-4",
                    title: t("agent-chat.suggestions.suggestion-4"),
                  },
                ]}
                onClose={() => setShowPanel(false)}
                onExpandedChange={setExpanded}
              />
            </Drawer>
          </ChatSessionProvider>
        )}
        {/* Page content */}
        {children}
      </section>
    </>
  );
};

export default Layout;
